import React from 'react';
import { setActiveLanguage } from 'react-localize-redux';
import {
  bool,
  func
} from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment/moment';
import Cookies from 'js-cookie';
import { Link, useLocation } from 'react-router-dom';

import {
  adminPermission, anySchemaPermission, hasPermission, hasSomePermission, surveyOverviewPermissionForAnySurvey
} from 'unihow-common';
import { withLang, langType } from '../../i18n';
import StickyBanner from '../StickyBanner';
import Menu from './Menu';
import UserName from './UserName';
import Logo from './Logo';
import { logOut } from '../../auth';
import { currentUserType } from '../../constants/types';
import envConfig from '../../constants/env';
import {
  AVAILABLE_LANGUAGES,
  LANG_COOKIE_NAME,
  PERSISTENT_COOKIE_LIFETIME,
  routes
} from '../../constants';

import unihowLogoUrl from '../../images/unihow-logo.svg';
import styles from './header.module.css';

const Header = ({
  userLoggedIn,
  currentUser,
  translate,
  lang,
  dispatchSetActiveLanguage
}) => {
  const location = useLocation();

  const { permissions: userPermissions } = currentUser;

  const handleLogOut = (e) => {
    e.preventDefault();
    logOut(envConfig.LOGIN_METHOD);
  };

  const links = [
    ...(hasSomePermission(userPermissions, surveyOverviewPermissionForAnySurvey()) ? [{ label: translate('header.navigation.surveys'), route: routes.surveys.index }] : []),
    ...(hasPermission(userPermissions, anySchemaPermission()) ? [{ label: translate('header.navigation.surveySchemas'), route: routes.surveySchemas.index }] : []),
    ...(hasPermission(userPermissions, adminPermission()) ? [{ label: translate('header.navigation.adminTools'), route: routes.adminTools.index }] : [])
  ];

  return (
    <header className={styles.header} role="banner">
      <StickyBanner />
      <div className={styles.topHeader}>
        <div className={styles.headerContainer}>
          <Logo />
          <p>{envConfig.UNIVERSITY_DETAILS.universityTitle[lang]}</p>
        </div>
      </div>
      <div className={styles.subHeader} data-cy="header-subheader">
        <div className={styles.headerContainer}>
          <Link to="/" aria-label={translate('header.logo.label')}>
            <img
              className={styles.unihowLogo}
              src={unihowLogoUrl}
              alt={translate('header.logo.description')}
            />
          </Link>
          {links.length > 0 && (
            <>
              <ul className={styles.linkContainer}>
                {links.map((link) => (
                  <li key={link.route}>
                    <Link className={location.pathname === link.route ? styles.active : ''} to={link.route}>{link.label}</Link>
                  </li>
                ))}
              </ul>
              <div className={styles.mobileMenuTriggerContainer}>
                <Menu
                  trigger={(
                    <button type="button" className={styles.triggerButton} aria-label={translate('header.openMenu')}>
                      <span className="icon--hamburger" />
                    </button>
                  )}
                  closeTrigger={(
                    <button type="button" className={styles.triggerButton} aria-label={translate('header.closeMenu')}>
                      <span className="icon--remove" />
                    </button>
                  )}
                  menuContainerId="mobile-menu"
                  menuContainerClassName={`${styles.headerDropDown} ${styles.mobileMenu}`}
                >
                  <ul>
                    {links.map((link) => (
                      <li key={link.route}>
                        <Link to={link.route} className={styles.menuOption} tabIndex="0">
                          <p>{link.label}</p>
                          {link.route === location.pathname ? <span className="icon--square-checked" /> : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Menu>
              </div>
            </>
          )}
          <Menu
            trigger={(
              <button type="button" data-cy="menu-dropdown" className={styles.triggerButton} aria-label={translate('userMenu.openLogoutMenu')}>
                {userLoggedIn ? <UserName userName={currentUser.name} /> : translate(`languageSelector.${lang}`)}
                <span className="icon--caret-down" />
              </button>
            )}
            closeTrigger={(
              <button type="button" className={styles.triggerButton} aria-label={translate('userMenu.closeLogoutMenu')}>
                {userLoggedIn ? <UserName userName={currentUser.name} /> : translate(`languageSelector.${lang}`)}
                <span className="icon--caret-up" />
              </button>
            )}
            menuContainerId="user-menu"
            menuContainerClassName={`${styles.headerDropDown} ${styles.userMenu}`}
          >
            <div>
              <div className={styles.menuTitle}><p>{translate('userMenu.changeLanguage')}</p></div>
              <ul>
                {AVAILABLE_LANGUAGES.map((langCode) => {
                  const isSelected = langCode === lang;
                  return (
                    <li key={langCode}>
                      <button type="button" className={styles.menuOption} tabIndex="0" aria-current={isSelected} onClick={() => dispatchSetActiveLanguage(langCode)}>
                        {translate(`languageSelector.${langCode}`)}
                        {isSelected ? <span className="icon--square-checked" aria-hidden /> : null}
                      </button>
                    </li>

                  );
                })}
              </ul>
              {userLoggedIn && (
              <>
                <hr className={styles.menuDivider} />
                <button type="button" className={styles.menuOption} tabIndex="0" onClick={(e) => handleLogOut(e)}>{translate('userMenu.logOut')}</button>
              </>
              )}
            </div>
          </Menu>
        </div>
      </div>
      <div className={styles.headerContainer} id="mobile-menu" />
      <div className={styles.headerContainer} id="user-menu" />
    </header>
  );
};

const mapStateToProps = ({ user }) => ({
  userLoggedIn: !user.pending && user.currentUser.name?.length > 0,
  currentUser: user.currentUser
});

Header.propTypes = {
  userLoggedIn: bool.isRequired,
  currentUser: currentUserType.isRequired,
  dispatchSetActiveLanguage: func.isRequired,
  ...langType
};

const mapDispatchToProps = (dispatch) => ({
  dispatchSetActiveLanguage: (language) => {
    dispatch(setActiveLanguage(language));
    Cookies.set(LANG_COOKIE_NAME, language, { expires: PERSISTENT_COOKIE_LIFETIME });
    document.documentElement.lang = language;
    moment.locale(language);
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withLang(Header));
